import {
	DBType,
	RestAPI,
	IVIPCapability,
	VIPCapability,
	IRestCollectionParams,
	IRestResult,
	IRestParams,
	IRestDeleteParams,
	IVIPCapabilityOptions,
	db,
	IErrorRestResponse,
	DEBUG,
	toaster,
	staticImplements,
	IEntityRest,
	VIPCapabilityFields
} from "@/loader";
import axios, { AxiosError } from "axios";

export interface IVIPCapabilityRestGetParams extends IRestParams {
	options?: IVIPCapabilityOptions;
}

export interface IVIPCapabilityRestCollectionParams extends IRestCollectionParams, IVIPCapabilityRestGetParams  {
	orderby?: VIPCapabilityFields | VIPCapabilityFields[];
	id?: number;
	fk_ip?: number;
	fk_capability_request?: number;
	status?: string;
	date?: string;
	extension?: boolean;
	expired?: boolean;
	renewable?: boolean;
	renewed?: boolean;
}

@staticImplements<IEntityRest<IVIPCapability, VIPCapability>>()
export abstract class VIPCapabilityRest {
	public static readonly endpoint: string = "capabilities";

	public static getURL(data?: IVIPCapability | VIPCapability, singular: boolean = false): string {
		let url: string[] = [RestAPI.url.replace(/\/$/, ""), RestAPI.namespace];
		url.push(VIPCapabilityRest.endpoint);
		if (singular && data?.id) {
			url.push(data.id.toString());
		}
		return url.join("/");
	}

	public static getStatusURL(id?: number): string {
		if (!id || Number.isNaN(id)) {
			throw new Error("The Capability must have a valid ID");
		}
		let url: string[] = [RestAPI.url.replace(/\/$/, ""), RestAPI.namespace, VIPCapabilityRest.endpoint, id.toString(), "status"];
		return url.join("/");
	}

	public static async list(params?: IVIPCapabilityRestCollectionParams): Promise<IRestResult<VIPCapability>> {
		return await RestAPI.list<IVIPCapability, VIPCapability>(VIPCapabilityRest.getURL(), DBType.capabilities, params);
	}

	public static async get(data: IVIPCapability | VIPCapability, params?: IVIPCapabilityRestGetParams): Promise<IRestResult<VIPCapability>> {
		return await RestAPI.get<IVIPCapability, VIPCapability>(VIPCapabilityRest.getURL(data, true), DBType.capabilities, params);
	}

	public static async create(data: IVIPCapability | VIPCapability): Promise<IRestResult<VIPCapability>> {
		return await RestAPI.post<IVIPCapability, VIPCapability>(VIPCapabilityRest.getURL(data), data, DBType.capabilities);
	}

	public static async update(data: IVIPCapability | VIPCapability): Promise<IRestResult<VIPCapability>> {
		return await RestAPI.post<IVIPCapability, VIPCapability>(VIPCapabilityRest.getURL(data, true), data, DBType.capabilities);
	}

	public static async delete(data: IVIPCapability | VIPCapability, params?: IRestDeleteParams): Promise<IRestResult<VIPCapability>> {
		return await RestAPI.delete<IVIPCapability, VIPCapability>(VIPCapabilityRest.getURL(data, true), DBType.capabilities, params);
	}

	public static async changeStatus(capability: IVIPCapability | VIPCapability): Promise<IRestResult<IVIPCapability>> {
		return await axios.post<IVIPCapability>(
			VIPCapabilityRest.getStatusURL(capability.id),
			{
				status: capability.status,
				date: capability.date?.format("YYYY-MM-DD") || undefined,
				event: capability.event,
				options: { events: { event: true } }
			}
		).then(response => {
			let item = db.capabilities.insertOrUpdate(response.data);
			return { success: true, item };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			toaster.toast({ message: error.response?.data.message || "Une erreur inconnue est survenue.", variant: "danger" });
			return { success: false, error: error.response?.data };
		});
	}

	public static async getPossibilities(): Promise<IRestResult<IVIPCapability>> {
		return await axios.get<IVIPCapability[]>(
			VIPCapabilityRest.getURL() + "/possibilities"
		).then(response => {
			let items = db.capabilities.insertOrUpdateMany(response.data);
			return { success: true, items, total: items.length };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			toaster.toast({ message: error.response?.data.message || "Une erreur inconnue est survenue.", variant: "danger" });
			return { success: false, error: error.response?.data };
		});
	}
}
